// Cloud: overview, resources, deployments, issues and audit over the cloud admin API.
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  DollarSign,
  RefreshCw,
  Rocket,
  Search,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import {
  useAckIssueMutation,
  useCloudAuditQuery,
  useCloudDeploymentsQuery,
  useCloudIssuesQuery,
  useCloudOverviewQuery,
  useCloudRefresh,
  useCloudResourcesQuery,
  useCloudSpendQuery,
  useCloudTrendsQuery,
} from '@/client/api/cloud/cloud.query';
import {
  CategoryGroup,
  CloudIssue,
  CloudResource,
  CloudWindow,
  DeployCoverage,
  Deployment,
  HealthLevel,
  HealthTally,
  TrendWindow,
} from '@/client/types/cloud';
import {
  ACTION_LABEL,
  AccessDenied,
  AppActions,
  AppLink,
  ConsoleLink,
  FetchingChip,
  LoadingNote,
  MetricChart,
  ResourceDialog,
  RowMenu,
  Sparkline,
  CLOUD_BASE,
  Card,
  EmptyRow,
  EnvBadge,
  ErrorNotice,
  HEALTH,
  HealthDot,
  HealthPill,
  HealthReasons,
  HealthSummary,
  INPUT_CLASS,
  KIND_LABEL,
  LEVELS,
  PageShell,
  PhaseBadge,
  ReasonDialog,
  ResourceName,
  RunsChart,
  StatCard,
  Tabs,
  Th,
  UsagePct,
  auditDetail,
  deploymentCause,
  detailPath,
  fmtAgo,
  fmtDur,
  fmtNum,
  fmtRate,
  fmtTime,
} from './cloudUi';

type Tab = 'overview' | 'resources' | 'deployments' | 'issues' | 'audit';
const TABS: Tab[] = ['overview', 'resources', 'deployments', 'issues', 'audit'];
const WINDOWS: readonly CloudWindow[] = ['24h', '7d', '30d'];
const RANK: Record<HealthLevel, number> = { red: 0, yellow: 1, green: 2, grey: 3 };

/** How a row opens its resource, and whether the viewer may act on it. */
const RowContext = createContext<{
  open: (r: CloudResource) => void;
  canOperate: boolean;
  canManage: boolean;
}>({ open: () => undefined, canOperate: false, canManage: false });

/** The name, as the way in: an app goes to its page, anything else opens its details. */
const OpenName: React.FC<{ r: CloudResource }> = ({ r }) => {
  const { open } = useContext(RowContext);
  const page = detailPath(r);
  return page ? (
    <Link to={page} className="font-medium text-gray-900 hover:underline">
      {r.name}
    </Link>
  ) : (
    <button
      type="button"
      onClick={() => open(r)}
      className="text-left font-medium text-gray-900 hover:underline"
    >
      {r.name}
    </button>
  );
};

/** The last cell of every resource row: what can be done from here. */
const RowEnd: React.FC<{ r: CloudResource }> = ({ r }) => {
  const { open, canOperate, canManage } = useContext(RowContext);
  return (
    <td className="w-12 px-4 py-2 text-right">
      <RowMenu
        resource={r}
        canOperate={canOperate}
        canManage={canManage}
        onDetails={() => open(r)}
      />
    </td>
  );
};

const tallyOf = (rows: CloudResource[]): HealthTally => {
  const out: HealthTally = { red: 0, yellow: 0, green: 0, grey: 0 };
  rows.forEach((r) => {
    out[r.health.level] += 1;
  });
  return out;
};

/** Red, yellow and green counts on one line; zero counts are left out. */
const TallyLine: React.FC<{ tally?: Partial<HealthTally> }> = ({ tally }) => (
  <span className="inline-flex items-center gap-3 text-xs text-gray-600">
    {LEVELS.filter((l) => tally?.[l]).map((l) => (
      <span key={l} className="inline-flex items-center gap-1" title={HEALTH[l].label}>
        <HealthDot level={l} />
        {tally?.[l]}
      </span>
    ))}
  </span>
);

const Unreadable: React.FC<{ kinds?: string[] }> = ({ kinds }) =>
  kinds?.length ? (
    <p className="text-xs text-amber-700">
      Could not read: {kinds.join(', ')}. Those resources are missing from this view, not healthy.
    </p>
  ) : null;

const CoverageNote: React.FC<{ coverage?: DeployCoverage }> = ({ coverage }) =>
  coverage && !coverage.complete ? (
    <p className="text-xs text-amber-700">
      Only the latest 20 deployments per app are read, which is less than this window for:{' '}
      {coverage.apps.join(', ')}. Their figures are low.
    </p>
  ) : null;

/* ---------- deployments table (overview and its own tab) ---------- */

const DeploymentsTable: React.FC<{ rows: Deployment[]; loading?: boolean; empty: string }> = ({
  rows,
  loading,
  empty,
}) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
          <Th>App</Th>
          <Th>Outcome</Th>
          <Th>Change</Th>
          <Th>By</Th>
          <Th>Started</Th>
          <Th right>Took</Th>
          <Th>{null}</Th>
        </tr>
      </thead>
      <tbody>
        {loading && (
          <EmptyRow cols={7} loading>
            {null}
          </EmptyRow>
        )}
        {!loading && rows.length === 0 && <EmptyRow cols={7}>{empty}</EmptyRow>}
        {rows.map((d) => (
          <tr key={d.id} className="border-b border-gray-50 align-top last:border-0">
            <td className="px-4 py-2">
              {d.app_id && <AppLink id={d.app_id} name={d.app_name ?? d.app_id} />}{' '}
              <EnvBadge env={d.environment} />
            </td>
            <td className="px-4 py-2">
              <PhaseBadge phase={d.phase} />
              {d.error && (
                <p className="mt-0.5 max-w-xs text-xs text-red-700">
                  {d.error.message ?? d.error.code} ({d.error.step})
                </p>
              )}
            </td>
            <td className="max-w-md truncate px-4 py-2 text-gray-700" title={deploymentCause(d)}>
              {deploymentCause(d)}
            </td>
            <td className="px-4 py-2 text-gray-600">{d.by ?? '—'}</td>
            <td className="whitespace-nowrap px-4 py-2 text-gray-600" title={fmtTime(d.created_at)}>
              {fmtAgo(d.created_at)}
            </td>
            <td className="px-4 py-2 text-right text-gray-600">{fmtDur(d.duration)}</td>
            <td className="whitespace-nowrap px-4 py-2 text-right">
              {d.app_id && (
                <Link
                  to={`${CLOUD_BASE}/apps/${encodeURIComponent(d.app_id)}?log=${encodeURIComponent(d.id)}`}
                  className="text-xs text-blue-600 hover:underline"
                >
                  {d.phase === 'ERROR' ? 'See why it failed' : 'Build log'}
                </Link>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/* ---------- overview ---------- */

/** The provider's sections in its order, each holding its categories. */
const bySection = (groups: CategoryGroup[]) => {
  const out: { section: string; groups: CategoryGroup[] }[] = [];
  // An API still on the previous release sends an object here; render nothing for it.
  (Array.isArray(groups) ? groups : []).forEach((g) => {
    const last = out[out.length - 1];
    if (last && last.section === g.section) last.groups.push(g);
    else out.push({ section: g.section, groups: [g] });
  });
  return out;
};

const ScopeNote: React.FC<{ scope?: string; exempt?: number; misfiled?: string[] }> = ({
  scope,
  exempt,
  misfiled,
}) =>
  scope ? (
    <div className="space-y-1">
      <p className="text-xs text-gray-500">
        Showing the {scope} project only.
        {exempt ? ` ${exempt} apps, databases and Droplets in other projects are exempt.` : ''}
      </p>
      {!!misfiled?.length && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Named for {scope} but filed under another project, so not shown here:{' '}
          {misfiled.join(', ')}. Move them to the {scope} project at the provider.
        </p>
      )}
    </div>
  ) : null;

/**
 * The provider console's own menu: every category we read, with its count and
 * health. A category holding nothing is shown as empty, and is a filter when
 * `onPick` is given.
 */
const CategoryMenu: React.FC<{
  groups: CategoryGroup[];
  active?: string;
  onPick?: (category: string) => void;
}> = ({ groups, active = '', onPick }) => (
  <div className="space-y-3">
    {bySection(groups).map((s) => (
      <div key={s.section}>
        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
          {s.section}
        </p>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4 xl:grid-cols-7">
          {s.groups.map((g) => {
            const on = active === g.category;
            const cls = `rounded-xl border bg-white px-3 py-2 text-left shadow-sm ${
              on ? 'border-blue-500 ring-1 ring-blue-200' : 'border-gray-100'
            } ${g.total === 0 ? 'opacity-60' : ''}`;
            const body = (
              <>
                <p className="truncate text-xs font-medium text-gray-600">{g.category}</p>
                <p className="text-lg font-semibold text-gray-900">{g.total}</p>
                {g.total === 0 ? (
                  <span className="text-xs text-gray-400">none</span>
                ) : (
                  <TallyLine tally={g} />
                )}
              </>
            );
            return onPick ? (
              <button
                key={g.category}
                type="button"
                aria-pressed={on}
                disabled={g.total === 0}
                onClick={() => onPick(on ? '' : g.category)}
                className={`${cls} ${g.total ? 'hover:bg-gray-50' : ''}`}
              >
                {body}
              </button>
            ) : (
              <div key={g.category} className={cls}>
                {body}
              </div>
            );
          })}
        </div>
      </div>
    ))}
  </div>
);

/* ---------- spend ---------- */

const money = (n?: number | null) =>
  n == null
    ? '—'
    : `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** A list of amounts as bars against the largest, so the dear ones stand out. */
const Bars: React.FC<{ rows: { name: string; amount: number | null }[] }> = ({ rows }) => {
  const top = Math.max(1, ...rows.map((r) => r.amount ?? 0));
  return (
    <ul className="space-y-2">
      {rows.map((r) => (
        <li key={r.name} className="text-sm">
          <div className="flex justify-between gap-3">
            <span className="truncate text-gray-700" title={r.name}>
              {r.name}
            </span>
            <span className="shrink-0 text-gray-900">{money(r.amount)}</span>
          </div>
          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-gray-100">
            <div
              className="h-full rounded-full bg-teal-500"
              style={{ width: `${Math.round(((r.amount ?? 0) / top) * 100)}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
};

const SpendSection: React.FC = () => {
  const q = useCloudSpendQuery();
  const s = q.data?.spend;
  const history = q.data?.history ?? [];
  return (
    <>
      <div className="pt-2">
        <h2 className="text-sm font-semibold text-gray-800">Spend</h2>
        <p className="text-xs text-gray-500">
          {s
            ? `What the ${s.scope} project costs, from the provider's invoice lines. ${money(s.unfiled)} this month is filed under no project (support, taxes and the like) and is not counted here.`
            : 'From the provider\u2019s invoice lines.'}
        </p>
      </div>
      {q.isError && <ErrorNotice error={q.error} onRetry={() => q.refetch()} />}
      {q.isLoading ? (
        <>
          <LoadingNote>Reading this month&apos;s invoice lines and six past invoices.</LoadingNote>
          <Skeleton className="h-40 w-full" />
        </>
      ) : !s ? (
        !q.isError && (
          <p className="rounded-xl border border-gray-100 bg-white p-6 text-center text-sm text-gray-400 shadow-sm">
            The provider token cannot read billing.
          </p>
        )
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
          <Card title="By month" note="past invoices, then this month so far">
            <Bars
              rows={[
                ...history.map((h) => ({ name: h.period, amount: h.project })),
                { name: 'This month so far', amount: s.month_to_date },
              ]}
            />
          </Card>
          <Card title="By product" note="this month">
            <Bars rows={s.by_product} />
          </Card>
          <Card title="Dearest resources" note="this month">
            <Bars rows={s.top} />
          </Card>
        </div>
      )}
    </>
  );
};

/* ---------- trends ---------- */

const TREND_WINDOWS: readonly TrendWindow[] = ['6h', '24h', '7d'];
const TREND_HOURS: Record<TrendWindow, number> = { '6h': 6, '24h': 24, '7d': 168 };

const TrendsDashboard: React.FC = () => {
  const [window, setWindow] = useState<TrendWindow>('24h');
  const q = useCloudTrendsQuery(window);
  const { canOperate } = useContext(RowContext);
  const apps = q.data?.apps ?? [];
  // The six lines worth drawing: whoever peaked highest on that measure.
  const top = (key: 'memory' | 'cpu') =>
    [...apps]
      .filter((a) => a[key]?.points.length)
      .sort((x, y) => (y[key]?.peak ?? 0) - (x[key]?.peak ?? 0))
      .slice(0, 6)
      .map((a) => ({ component: a.name, instance: a.name, points: a[key]!.points }));
  const totalRestarts = (q.data?.restarts ?? []).reduce((n, p) => n + p[1], 0);
  const hours = TREND_HOURS[window];
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
        <div>
          <h2 className="text-sm font-semibold text-gray-800">Trends</h2>
          <p className="text-xs text-gray-500">
            Each line is an app&apos;s busiest instance. The dashed line is the 85% warning mark.
          </p>
        </div>
        <Tabs value={window} onChange={setWindow} options={TREND_WINDOWS} />
      </div>
      {q.isError && <ErrorNotice error={q.error} onRetry={() => q.refetch()} />}
      {q.data?.unavailable && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          <span>
            DigitalOcean returned no metrics for any app, usually because its rate limit was hit.
            This is missing data, not a quiet fleet.
          </span>
          <Button size="sm" variant="outline" onClick={() => q.refetch()}>
            <RefreshCw className="mr-2 h-3.5 w-3.5" />
            Retry
          </Button>
        </div>
      )}
      {q.isLoading ? (
        <>
          <LoadingNote>Reading CPU, memory and restart history for every app.</LoadingNote>
          <Skeleton className="h-56 w-full" />
        </>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <Card title="Memory %" note="six highest">
              <MetricChart series={top('memory')} percent spanHours={hours} />
            </Card>
            <Card title="CPU %" note="six highest">
              <MetricChart series={top('cpu')} percent spanHours={hours} />
            </Card>
            <Card title="Restarts" note={`${totalRestarts} across all apps`}>
              <MetricChart
                series={[
                  { component: 'all apps', instance: 'Restarts', points: q.data?.restarts ?? [] },
                ]}
                spanHours={hours}
              />
            </Card>
          </div>
          <Card title="Every app" note={`${window}, highest memory first`} flush>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
                    <Th>App</Th>
                    <Th>Memory</Th>
                    <Th right>Now / peak</Th>
                    <Th>CPU</Th>
                    <Th right>Now / peak</Th>
                    <Th right>Restarts</Th>
                    <Th>{null}</Th>
                  </tr>
                </thead>
                <tbody>
                  {apps.length === 0 && <EmptyRow cols={7}>No app is reporting metrics</EmptyRow>}
                  {apps.map((a) => (
                    <tr key={a.id} className="border-b border-gray-50 last:border-0">
                      <td className="px-4 py-2">
                        <span className="inline-flex items-center gap-2">
                          <HealthDot level={a.health} />
                          <AppLink id={a.id} name={a.name} />
                          <EnvBadge env={a.environment} />
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <Sparkline points={a.memory?.points} label={`${a.name} memory`} />
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-right">
                        <UsagePct pct={a.memory?.now} /> / <UsagePct pct={a.memory?.peak} />
                      </td>
                      <td className="px-4 py-2">
                        <Sparkline points={a.cpu?.points} label={`${a.name} CPU`} />
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-right">
                        <UsagePct pct={a.cpu?.now} /> / <UsagePct pct={a.cpu?.peak} />
                      </td>
                      <td
                        className={`px-4 py-2 text-right ${
                          a.restarts ? 'font-medium text-amber-700' : 'text-gray-600'
                        }`}
                      >
                        {a.restarts ?? '—'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-right">
                        <AppActions app={a} canOperate={canOperate} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </>
  );
};

const OverviewTab: React.FC<{ window: CloudWindow }> = ({ window }) => {
  const q = useCloudOverviewQuery(window);
  const d = q.data;
  if (q.isError && !d) return <ErrorNotice error={q.error} onRetry={() => q.refetch()} />;
  if (q.isLoading) {
    return (
      <>
        <LoadingNote />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-64 w-full" />
      </>
    );
  }
  return (
    <>
      <ScopeNote scope={d?.scope} exempt={d?.exempt} misfiled={d?.misfiled} />
      <Unreadable kinds={d?.unreadable} />
      <HealthSummary tally={d?.health} />
      <CategoryMenu groups={d?.groups ?? []} />
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-6">
        {(['production', 'staging'] as const).map((env) => (
          <div key={env} className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
            <p className="text-xs font-medium capitalize text-gray-500">{env}</p>
            <p className="text-xl font-semibold text-gray-900">
              {d ? LEVELS.reduce((n, l) => n + d.environments[env][l], 0) : '—'}
            </p>
            <TallyLine tally={d?.environments[env]} />
          </div>
        ))}
      </div>

      <Card
        title="Needs attention"
        note={d ? `${d.issues.open} open issues, ${d.issues.acknowledged} acknowledged` : undefined}
        flush
      >
        <table className="w-full text-sm">
          <tbody>
            {q.isLoading && (
              <EmptyRow cols={4} loading>
                {null}
              </EmptyRow>
            )}
            {d && d.attention.length === 0 && (
              <EmptyRow cols={4}>Nothing is red or yellow right now</EmptyRow>
            )}
            {(d?.attention ?? []).map((r) => (
              <tr key={`${r.kind}:${r.id}`} className="border-b border-gray-50 last:border-0">
                <td className="w-28 px-4 py-2">
                  <HealthPill level={r.health.level} />
                </td>
                <td className="px-4 py-2">
                  <OpenName r={r} /> <EnvBadge env={r.environment} />
                  <p className="text-xs text-gray-500">{r.category}</p>
                </td>
                <td className="px-4 py-2">
                  <HealthReasons health={r.health} />
                </td>
                <RowEnd r={r} />
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <TrendsDashboard />

      <h2 className="pt-2 text-sm font-semibold text-gray-800">Deployments and spend</h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <StatCard
          label={`Deployments (${window})`}
          value={fmtNum(d?.deployments.total)}
          hint={d ? `${d.deployments.canceled} superseded or cancelled` : undefined}
          icon={<Rocket className="h-5 w-5 text-blue-600" />}
          color="bg-blue-50"
        />
        <StatCard
          label="Deploy success"
          value={fmtRate(d?.deployments.success_rate)}
          icon={<CheckCircle2 className="h-5 w-5 text-green-600" />}
          color="bg-green-50"
        />
        <StatCard
          label="Failed deployments"
          value={fmtNum(d?.deployments.failed)}
          icon={<AlertTriangle className="h-5 w-5 text-red-600" />}
          color="bg-red-50"
        />
        <StatCard
          label="Time to deploy"
          value={fmtDur(d?.deployments.avg_duration)}
          hint={
            d?.deployments.p95_duration != null
              ? `p95 ${fmtDur(d.deployments.p95_duration)}`
              : undefined
          }
          icon={<Clock className="h-5 w-5 text-violet-600" />}
          color="bg-violet-50"
        />
        <StatCard
          label={`${d?.spend?.scope ?? 'Our'} spend this month`}
          value={d?.spend ? money(d.spend.month_to_date) : '—'}
          hint={
            d?.spend
              ? `of $${d.spend.account_month_to_date ?? '?'} for the whole account`
              : 'billing not readable'
          }
          icon={<DollarSign className="h-5 w-5 text-teal-600" />}
          color="bg-teal-50"
        />
      </div>
      <CoverageNote coverage={d?.coverage} />
      <Card title="Deployments" note={window === '24h' ? 'per hour' : 'per day'}>
        {q.isLoading ? (
          <Skeleton className="h-56 w-full" />
        ) : (
          <RunsChart series={d?.deployments.series ?? []} window={window === '24h' ? '24h' : '30d'} />
        )}
      </Card>
      <Card title="Latest deployments" flush>
        <DeploymentsTable
          rows={d?.recent_deployments ?? []}
          loading={q.isLoading}
          empty="No deployments yet"
        />
      </Card>
      <SpendSection />
    </>
  );
};

/* ---------- resources ---------- */

const worstFirst = (rows: CloudResource[]) =>
  [...rows].sort(
    (a, b) => RANK[a.health.level] - RANK[b.health.level] || a.name.localeCompare(b.name),
  );

const HealthCell: React.FC<{ r: CloudResource }> = ({ r }) => (
  <td className="w-32 whitespace-nowrap px-4 py-2">
    <HealthPill level={r.health.level} />
  </td>
);

const NameCell: React.FC<{ r: CloudResource; sub?: React.ReactNode }> = ({ r, sub }) => (
  <td className="px-4 py-2">
    <OpenName r={r} />
    <p className="text-xs text-gray-500">{sub}</p>
    <HealthReasons health={r.health} className="mt-0.5" />
  </td>
);

const componentSummary = (r: CloudResource) => {
  const counts: Record<string, number> = {};
  (r.components ?? []).forEach((c) => {
    counts[c.type] = (counts[c.type] ?? 0) + 1;
  });
  return Object.entries(counts)
    .map(([type, n]) => `${n} ${type.replace('_', ' ')}${n > 1 ? 's' : ''}`)
    .join(', ');
};

const AppsTable: React.FC<{ rows: CloudResource[] }> = ({ rows }) => (
  <table className="w-full text-sm">
    <thead>
      <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
        <Th>Health</Th>
        <Th>App</Th>
        <Th>Runs</Th>
        <Th right>Memory</Th>
        <Th right>Restarts (1h)</Th>
        <Th>Last deployment</Th>
        <Th>{null}</Th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <tr key={r.id} className="border-b border-gray-50 align-top last:border-0">
          <HealthCell r={r} />
          <NameCell r={r} sub={[r.environment, r.region, r.tier].filter(Boolean).join(', ')} />
          <td className="px-4 py-2 text-gray-600">{componentSummary(r)}</td>
          <td className="px-4 py-2 text-right">
            <UsagePct pct={r.metrics?.memory_pct} />
          </td>
          <td className="px-4 py-2 text-right text-gray-700">{r.metrics?.restarts ?? '—'}</td>
          <td className="px-4 py-2 text-gray-600">
            {r.in_progress_deployment ? (
              <PhaseBadge phase={r.in_progress_deployment.phase} />
            ) : r.active_deployment ? (
              <span title={fmtTime(r.active_deployment.created_at)}>
                {fmtAgo(r.active_deployment.created_at)}
                {r.active_deployment.commit ? `, ${r.active_deployment.commit.slice(0, 7)}` : ''}
              </span>
            ) : (
              'never'
            )}
          </td>
          <RowEnd r={r} />
        </tr>
      ))}
    </tbody>
  </table>
);

const DatabasesTable: React.FC<{ rows: CloudResource[] }> = ({ rows }) => (
  <table className="w-full text-sm">
    <thead>
      <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
        <Th>Health</Th>
        <Th>Database</Th>
        <Th>Engine</Th>
        <Th>Size</Th>
        <Th right>Storage</Th>
        <Th>Maintenance</Th>
        <Th>{null}</Th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <tr key={r.id} className="border-b border-gray-50 align-top last:border-0">
          <HealthCell r={r} />
          <NameCell r={r} sub={[r.region, r.status].filter(Boolean).join(', ')} />
          <td className="px-4 py-2 text-gray-700">
            {r.engine} {r.version}
          </td>
          <td className="px-4 py-2 text-gray-600">
            {r.size}
            {r.nodes && r.nodes > 1 ? ` x ${r.nodes}` : ''}
          </td>
          <td className="px-4 py-2 text-right text-gray-600">
            {r.storage_mib ? `${Math.round(r.storage_mib / 1024)} GB` : '—'}
          </td>
          <td className="px-4 py-2 capitalize text-gray-600">
            {r.maintenance?.day ? `${r.maintenance.day} ${r.maintenance.hour} UTC` : '—'}
          </td>
          <RowEnd r={r} />
        </tr>
      ))}
    </tbody>
  </table>
);

const ServersTable: React.FC<{ rows: CloudResource[] }> = ({ rows }) => (
  <table className="w-full text-sm">
    <thead>
      <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
        <Th>Health</Th>
        <Th>Droplet</Th>
        <Th>Size</Th>
        <Th>Address</Th>
        <Th right>Memory</Th>
        <Th right>Disk</Th>
        <Th>{null}</Th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <tr key={r.id} className="border-b border-gray-50 align-top last:border-0">
          <HealthCell r={r} />
          <NameCell r={r} sub={[r.region, r.status].filter(Boolean).join(', ')} />
          <td className="px-4 py-2 text-gray-600">
            {r.vcpus} vCPU, {r.memory_mb ? Math.round(r.memory_mb / 1024) : '—'} GB, {r.disk_gb} GB
            disk
          </td>
          <td className="px-4 py-2 font-mono text-xs text-gray-600">{r.ip ?? '—'}</td>
          <td className="px-4 py-2 text-right">
            <UsagePct pct={r.metrics?.memory_pct} />
          </td>
          <td className="px-4 py-2 text-right">
            <UsagePct pct={r.metrics?.disk_pct} />
          </td>
          <RowEnd r={r} />
        </tr>
      ))}
    </tbody>
  </table>
);

/** Any category without figures of its own: what it is and one line about it. */
const OtherTable: React.FC<{ rows: CloudResource[] }> = ({ rows }) => {
  // Buckets, snapshots and domains report no state. A column of "not monitored"
  // on every row says nothing, so it only appears when something here is judged.
  const judged = rows.some((r) => r.health.level !== 'grey');
  return (
  <table className="w-full text-sm">
    <thead>
      <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
        {judged && <Th>Health</Th>}
        <Th>Name</Th>
        <Th>Type</Th>
        <Th>Detail</Th>
        <Th>{null}</Th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <tr key={`${r.kind}:${r.id}`} className="border-b border-gray-50 align-top last:border-0">
          {judged && <HealthCell r={r} />}
          <NameCell r={r} sub={r.region} />
          <td className="px-4 py-2 text-gray-600">{KIND_LABEL[r.kind]}</td>
          <td className="px-4 py-2 text-gray-600">{r.detail}</td>
          <RowEnd r={r} />
        </tr>
      ))}
    </tbody>
  </table>
);
};

const TABLES: Record<string, React.FC<{ rows: CloudResource[] }>> = {
  'App Platform': AppsTable,
  Databases: DatabasesTable,
  Droplets: ServersTable,
};

/** Production before staging, then worst first, so the list reads top-down by risk. */
const inOrder = (rows: CloudResource[]) =>
  worstFirst(rows).sort(
    (a, b) => Number(a.environment === 'staging') - Number(b.environment === 'staging'),
  );

const ResourcesTab: React.FC = () => {
  const q = useCloudResourcesQuery();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [level, setLevel] = useState<HealthLevel | ''>('');

  const all = q.data?.resources ?? [];
  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return all.filter(
      (r) =>
        (!category || r.category === category) &&
        (!level || r.health.level === level) &&
        (!needle || r.name.toLowerCase().includes(needle)),
    );
  }, [all, search, category, level]);

  if (q.isError && !q.data) return <ErrorNotice error={q.error} onRetry={() => q.refetch()} />;
  return (
    <>
      <ScopeNote
        scope={q.data?.scope}
        exempt={q.data?.exempt}
        misfiled={q.data?.misfiled}
      />
      <Unreadable kinds={q.data?.unreadable} />
      <HealthSummary
        tally={q.data ? tallyOf(category ? all.filter((r) => r.category === category) : all) : undefined}
        active={level}
        onPick={setLevel}
      />
      <CategoryMenu groups={q.data?.groups ?? []} active={category} onPick={setCategory} />
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search resources"
            aria-label="Search resources"
            className={`${INPUT_CLASS} w-56 pl-8`}
          />
        </div>
        <span className="ml-auto text-xs text-gray-500">
          {shown.length} resources{q.data ? `, read ${fmtAgo(q.data.as_of)}` : ''}
        </span>
      </div>
      {q.isLoading && (
        <>
          <LoadingNote />
          <Skeleton className="h-64 w-full" />
        </>
      )}
      {!q.isLoading && shown.length === 0 && (
        <div className="rounded-xl border border-gray-100 bg-white p-8 text-center text-sm text-gray-400 shadow-sm">
          No resources match these filters.
        </div>
      )}
      {bySection(q.data?.groups ?? []).map((s) => {
        const cards = s.groups
          .map((g) => ({ g, rows: shown.filter((r) => r.category === g.category) }))
          .filter((c) => c.rows.length > 0);
        if (!cards.length) return null;
        return (
          <React.Fragment key={s.section}>
            <h2 className="pt-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
              {s.section}
            </h2>
            {cards.map(({ g, rows }) => {
              const Table = TABLES[g.category] ?? OtherTable;
              return (
                <Card
                  key={g.category}
                  title={`${g.category} (${rows.length})`}
                  action={
                    rows.every((r) => r.health.level === 'grey') ? (
                      <span className="text-xs text-gray-400">not monitored</span>
                    ) : (
                      <TallyLine tally={tallyOf(rows)} />
                    )
                  }
                  flush
                >
                  <div className="overflow-x-auto">
                    <Table rows={inOrder(rows)} />
                  </div>
                </Card>
              );
            })}
          </React.Fragment>
        );
      })}
    </>
  );
};

/* ---------- deployments ---------- */

const DeploymentsTab: React.FC<{ window: CloudWindow }> = ({ window }) => {
  const q = useCloudDeploymentsQuery(window);
  const [phase, setPhase] = useState('');
  const rows = (q.data?.deployments ?? []).filter((d) => !phase || d.phase === phase);
  const s = q.data?.stats;
  if (q.isError && !q.data) return <ErrorNotice error={q.error} onRetry={() => q.refetch()} />;
  return (
    <>
      <CoverageNote coverage={q.data?.coverage} />
      <Card title="Deployments" note={window === '24h' ? 'per hour' : 'per day'}>
        {q.isLoading ? (
          <Skeleton className="h-56 w-full" />
        ) : (
          <RunsChart series={s?.series ?? []} window={window === '24h' ? '24h' : '30d'} />
        )}
      </Card>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-gray-500">
          {s
            ? `${s.succeeded} succeeded, ${s.failed} failed, ${s.canceled} superseded or cancelled, ${s.in_progress} in progress. Average ${fmtDur(s.avg_duration)}.`
            : ''}
        </p>
        <select
          value={phase}
          onChange={(e) => setPhase(e.target.value)}
          aria-label="Outcome"
          className={INPUT_CLASS}
        >
          <option value="">Any outcome</option>
          <option value="ERROR">Failed</option>
          <option value="ACTIVE">Live now</option>
          <option value="SUPERSEDED">Superseded</option>
          <option value="CANCELED">Cancelled</option>
        </select>
      </div>
      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        <DeploymentsTable
          rows={rows}
          loading={q.isLoading}
          empty="No deployments match in this window"
        />
      </div>
    </>
  );
};

/* ---------- issues ---------- */

/** The usual next step for each kind of issue, so a red row is not a dead end. */
const nextStep = (code: string): string => {
  if (code === 'memory_high')
    return 'Restart to clear it now. If it keeps coming back, look for a leak in the logs or scale the component up.';
  if (code === 'cpu_high')
    return 'Check the logs for a runaway request or job. If the load is real, add an instance.';
  if (code === 'restarts')
    return 'Read the run logs for the crash. Roll back if it started with the last deployment.';
  if (code === 'deploy_failed')
    return 'Open the build log for the cause, fix it, then deploy again. The previous release is still serving.';
  if (code === 'no_active_deployment') return 'Deploy the app; nothing is serving.';
  if (code === 'no_metrics') return 'Usually the provider lagging. Refresh in a few minutes.';
  if (code === 'disk_high') return 'Free space or grow the disk before it fills.';
  if (code.startsWith('certificate:') || code.startsWith('domain:'))
    return "Check the domain's DNS and certificate at the provider.";
  return 'Open it at the provider to see more.';
};

const IssueRow: React.FC<{ issue: CloudIssue; canOperate: boolean }> = ({ issue, canOperate }) => {
  const [acking, setAcking] = useState(false);
  const ack = useAckIssueMutation();
  const close = () => {
    setAcking(false);
    ack.reset();
  };
  return (
    <div
      className={`rounded-xl border border-l-4 border-gray-100 bg-white p-4 shadow-sm ${
        HEALTH[issue.status === 'open' ? issue.level : 'grey'].border
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <HealthPill level={issue.level} />
            <ResourceName resource={issue.resource} />
            <EnvBadge env={issue.resource.environment} />
            <span className="text-xs text-gray-500">{KIND_LABEL[issue.resource.kind]}</span>
            {issue.status !== 'open' && (
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                acknowledged
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-gray-800">{issue.text}</p>
          <p className="mt-0.5 text-sm text-gray-600">{nextStep(issue.code)}</p>
          <p className="mt-0.5 text-xs text-gray-500">
            {issue.at ? `Since ${fmtTime(issue.at)}` : 'Ongoing'}
            {issue.ack
              ? `. Acknowledged by ${issue.ack.by} on ${fmtTime(issue.ack.at)}${issue.ack.note ? `: ${issue.ack.note}` : ''}`
              : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {issue.resource.kind === 'app' ? (
            <AppActions app={issue.resource} canOperate={canOperate} />
          ) : (
            <ConsoleLink resource={issue.resource} />
          )}
          {canOperate && issue.status === 'open' && (
            <Button size="sm" onClick={() => setAcking(true)}>
              Acknowledge
            </Button>
          )}
        </div>
      </div>
      {acking && (
        <ReasonDialog
          open
          onClose={close}
          title={`Acknowledge: ${issue.resource.name}`}
          description={
            issue.at
              ? 'Marks this as seen. A new occurrence re-opens it.'
              : 'Marks this as seen for a day. It re-opens if the condition is still there.'
          }
          confirmLabel="Acknowledge"
          busy={ack.isPending}
          error={ack.error}
          onConfirm={(note) =>
            ack.mutate(
              { issueId: issue.id, note: note || undefined, resourceName: issue.resource.name },
              { onSuccess: close },
            )
          }
        />
      )}
    </div>
  );
};

const IssuesTab: React.FC<{ canOperate: boolean }> = ({ canOperate }) => {
  const [status, setStatus] = useState<'open' | 'acknowledged' | 'all'>('open');
  const q = useCloudIssuesQuery(status);
  const rows = q.data?.issues ?? [];
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-gray-500">
          Every reason a resource is red or yellow. An issue clears by itself when the cause does.
        </p>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as typeof status)}
          aria-label="Status"
          className={INPUT_CLASS}
        >
          <option value="open">Open</option>
          <option value="acknowledged">Acknowledged</option>
          <option value="all">All</option>
        </select>
      </div>
      {q.isError && <ErrorNotice error={q.error} onRetry={() => q.refetch()} />}
      {q.isLoading && (
        <>
          <LoadingNote />
          <Skeleton className="h-32 w-full" />
        </>
      )}
      {!q.isLoading && !q.isError && rows.length === 0 && (
        <div className="rounded-xl border border-gray-100 bg-white p-8 text-center text-sm text-gray-400 shadow-sm">
          No {status === 'all' ? '' : status} issues.
        </div>
      )}
      <div className="space-y-3">
        {rows.map((i) => (
          <IssueRow key={i.id} issue={i} canOperate={canOperate} />
        ))}
      </div>
    </>
  );
};

/* ---------- audit ---------- */

const AuditTab: React.FC = () => {
  const q = useCloudAuditQuery(200);
  const rows = q.data?.rows ?? [];
  return (
    <>
      {q.isError && <ErrorNotice error={q.error} onRetry={() => q.refetch()} />}
      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
                <Th>When</Th>
                <Th>Who</Th>
                <Th>Action</Th>
                <Th>Resource</Th>
                <Th>Reason</Th>
              </tr>
            </thead>
            <tbody>
              {q.isLoading && (
                <EmptyRow cols={5} loading>
                  {null}
                </EmptyRow>
              )}
              {!q.isLoading && !q.isError && rows.length === 0 && (
                <EmptyRow cols={5}>No console actions recorded yet</EmptyRow>
              )}
              {rows.map((r, i) => (
                <tr key={`${r.at}-${i}`} className="border-b border-gray-50 last:border-0">
                  <td className="whitespace-nowrap px-4 py-2 text-gray-500">{fmtTime(r.at)}</td>
                  <td className="px-4 py-2 text-gray-700">{r.by}</td>
                  <td className="px-4 py-2 text-gray-900">
                    {ACTION_LABEL[r.action] ?? r.action}
                    <span className="block break-all text-xs text-gray-500">{auditDetail(r)}</span>
                  </td>
                  <td className="px-4 py-2">
                    <ResourceName
                      resource={{
                        kind: r.kind,
                        id: r.resource_id,
                        name: r.resource_name || r.resource_id,
                      }}
                    />
                  </td>
                  <td className="px-4 py-2 text-gray-600">{r.reason || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

/* ---------- page ---------- */

const CloudPage: React.FC<{ initialTab?: Tab }> = ({ initialTab = 'overview' }) => {
  useBreadcrumbs([
    { label: 'Cloud', href: CLOUD_BASE },
    ...(initialTab === 'overview'
      ? []
      : [{ label: initialTab.charAt(0).toUpperCase() + initialTab.slice(1) }]),
  ]);
  const viewPerm = useHasPermission('cloud.view');
  const operatePerm = useHasPermission('cloud.operate');
  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const canOperate = operatePerm.isSuperAdmin || operatePerm.hasPermission;
  const managePerm = useHasPermission('cloud.manage');
  const canManage = managePerm.isSuperAdmin || managePerm.hasPermission;

  const [tab, setTab] = useState<Tab>(initialTab);
  const [window, setWindow] = useState<CloudWindow>('7d');
  const [detail, setDetail] = useState<CloudResource | null>(null);
  const rowContext = useMemo(
    () => ({ open: setDetail, canOperate, canManage }),
    [canOperate, canManage],
  );
  useEffect(() => setTab(initialTab), [initialTab]);
  const refresh = useCloudRefresh();

  if (viewPerm.isLoading) {
    return (
      <PageShell title="Cloud" description="">
        <Skeleton className="h-64 w-full" />
      </PageShell>
    );
  }
  if (!canView) {
    return (
      <PageShell title="Cloud" description="">
        <AccessDenied />
      </PageShell>
    );
  }
  return (
    <PageShell
      title="Cloud"
      description="What FeelyFeely runs at DigitalOcean, filed the way its console files it: what is healthy, what needs a look, what was deployed, and what the console changed."
      actions={
        <div className="flex items-center gap-2">
          <FetchingChip />
          {(tab === 'overview' || tab === 'deployments') && (
            <Tabs value={window} onChange={setWindow} options={WINDOWS} />
          )}
          <Button
            variant="outline"
            size="sm"
            disabled={refresh.isPending}
            onClick={() => refresh.mutate()}
          >
            <RefreshCw className={`mr-2 h-3.5 w-3.5 ${refresh.isPending ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      }
    >
      <div className="flex gap-2 border-b border-gray-200">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium capitalize ${
              tab === t
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {t}
          </button>
        ))}
      </div>
      <RowContext.Provider value={rowContext}>
        {tab === 'overview' && <OverviewTab window={window} />}
        {tab === 'resources' && <ResourcesTab />}
      </RowContext.Provider>
      {detail && <ResourceDialog resource={detail} onClose={() => setDetail(null)} />}
      {tab === 'deployments' && <DeploymentsTab window={window} />}
      {tab === 'issues' && <IssuesTab canOperate={canOperate} />}
      {tab === 'audit' && <AuditTab />}
    </PageShell>
  );
};

export default CloudPage;
